<script lang="ts">
  import {
    groupDigits,
    type MeterInput,
    type MeterInputKind,
    type MeterResult,
  } from '../../lib/complexity';
  import { untrack } from 'svelte';
  import type { SandboxClient } from '../../lib/sandboxd';
  import { sandbox } from '../../services';
  import CodeEditor from '../CodeEditor.svelte';
  import OutputPanel from '../OutputPanel.svelte';
  import type { ResultSeries } from './chart-model';
  import MeterResults from './MeterResults.svelte';
  import {
    buildOwnMeter,
    DEFAULT_OWN_SIZES,
    normalizeSizes,
    OWN_PRESETS,
    presetById,
    SIZE_CHOICES,
    validateOwnOps,
    type OwnInitial,
  } from './own-code';
  import { runMeter } from './run-meter';
  import StateIcon from './StateIcon.svelte';

  /**
   * 自分のコードを測る（complexity-meter.md §2）。いちばん内側の文に viz.tick() を置いて実行し、
   * generateInput で作った N の列を 1 リクエストで送る。
   */
  interface Props {
    client?: Pick<SandboxClient, 'run'>;
    /** カードから開いたときの初期値。無ければ「2 重ループ」のプリセット。 */
    initial?: OwnInitial | undefined;
  }

  let { client = sandbox, initial }: Props = $props();
  const uid = $props.id();

  const start = untrack(() => {
    const p = presetById('loop2');
    return (
      initial ?? {
        code: p?.code ?? '',
        input: p?.input ?? { kind: 'n-only' },
        sizes: DEFAULT_OWN_SIZES,
        ops: p?.ops,
      }
    );
  });

  let presetId = $state(untrack(() => (initial ? 'card' : 'loop2')));
  let code = $state(start.code);
  let kind = $state<MeterInputKind>(start.input.kind);
  let valueMax = $state(start.input.valueMax ?? 999);
  let alphabet = $state(start.input.alphabet ?? 'ab');
  let ops = $state(start.ops ?? '');
  let chosen = $state<number[]>([...start.sizes]);

  let running = $state(false);
  let notice = $state<string | null>(null);
  let error = $state<{ message: string; retryable: boolean } | null>(null);
  let compileError = $state<string | null>(null);
  let measured = $state<{
    result: MeterResult;
    stdouts: string[];
    noTicks: boolean;
  } | null>(null);
  let controller: AbortController | null = null;

  $effect(() => () => controller?.abort());

  const sizes = $derived(normalizeSizes(chosen));
  const opsError = $derived(validateOwnOps(ops));
  const input = $derived.by((): MeterInput => {
    if (kind === 'n-and-array')
      return { kind, valueMin: 1, valueMax: Math.max(1, Math.floor(valueMax)) };
    if (kind === 'n-and-string') return { kind, alphabet: alphabet === '' ? 'ab' : alphabet };
    return { kind };
  });
  const canRun = $derived(!running && sizes !== null && opsError === null && code.trim() !== '');

  function applyPreset(id: string) {
    presetId = id;
    const p = presetById(id);
    if (!p) return;
    code = p.code;
    kind = p.input.kind;
    if (p.input.valueMax !== undefined) valueMax = p.input.valueMax;
    ops = p.ops;
    measured = null;
    compileError = null;
  }

  function toggleSize(n: number, on: boolean) {
    if (on) {
      if (chosen.length >= 10) return;
      chosen = [...chosen, n];
    } else {
      chosen = chosen.filter((x) => x !== n);
    }
  }

  async function measure() {
    if (!canRun || sizes === null) return;
    // 入力時の検証をすり抜けても、送る前にもう一度確かめる（planMeasurement が OpsError を投げないように）
    const invalid = validateOwnOps(ops);
    if (invalid !== null) {
      error = { message: invalid, retryable: false };
      return;
    }
    running = true;
    error = null;
    notice = null;
    controller = new AbortController();
    let out: Awaited<ReturnType<typeof runMeter>>;
    try {
      out = await runMeter(client, {
        code,
        meter: buildOwnMeter(input, sizes, ops),
        purpose: 'playground',
        span: { mode: 'own' },
        bypassCache: measured !== null,
        signal: controller.signal,
        onRetry: (t) => (notice = t),
      });
    } catch {
      error = {
        message: '計測できませんでした。設定を見直して、もう一度試してください。',
        retryable: true,
      };
      return;
    } finally {
      running = false;
      notice = null;
      controller = null;
    }
    switch (out.kind) {
      case 'ok':
        compileError = null;
        measured = {
          result: out.result,
          stdouts: out.stdouts,
          noTicks: out.noTicks,
        };
        break;
      case 'compile-error':
        compileError = out.output;
        break;
      case 'error':
        error = { message: out.message, retryable: out.retryable };
        break;
      case 'nothing-to-send':
        error = {
          message:
            '送れる N がありませんでした（入力が大きすぎるか、見積もりで時間切れになるためです）。',
          retryable: false,
        };
        break;
      case 'aborted':
        break;
    }
  }

  const series = $derived.by((): ResultSeries[] =>
    measured
      ? [
          {
            key: 1,
            label: 'このコード',
            points: measured.result.points,
            skipped: measured.result.skipped,
            ops: ops.trim() === '' || opsError ? undefined : ops.trim(),
          },
        ]
      : [],
  );
  const firstOut = $derived(measured?.stdouts[0] ?? '');
  const firstN = $derived(measured?.result.points[0]?.n);
</script>

<div class="own">
  <div class="field">
    <label for="{uid}-preset">ひな形</label>
    <select
      id="{uid}-preset"
      value={presetId}
      data-testid="own-preset"
      onchange={(e) => {
        const el = e.currentTarget;
        if (el instanceof HTMLSelectElement) applyPreset(el.value);
      }}
    >
      {#if initial}<option value="card">カードのコード</option>{/if}
      {#each OWN_PRESETS as p (p.id)}
        <option value={p.id}>{p.label}</option>
      {/each}
    </select>
  </div>

  <CodeEditor bind:value={code} minLines={10} label="計測する Swift のコード" onsubmit={measure} />
  <p class="text-soft small">
    いちばん内側のループの中に <code>viz.tick()</code> を書くと、その行が実行された回数をステップ数として数えます。
  </p>

  <div class="settings">
    <div class="field">
      <label for="{uid}-kind">入力</label>
      <select id="{uid}-kind" bind:value={kind}>
        <option value="n-only">N だけ（1 行目に N）</option>
        <option value="n-and-array">N と N 個の整数</option>
        <option value="n-and-string">N と N 文字の文字列</option>
      </select>
    </div>
    {#if kind === 'n-and-array'}
      <div class="field">
        <label for="{uid}-max">整数の最大（最小は 1）</label>
        <input id="{uid}-max" type="number" min="1" class="num" bind:value={valueMax} />
      </div>
    {:else if kind === 'n-and-string'}
      <div class="field">
        <label for="{uid}-alpha">使う文字</label>
        <input id="{uid}-alpha" type="text" class="mono" bind:value={alphabet} />
      </div>
    {/if}
    <div class="field">
      <label for="{uid}-ops"
        >回数の式（任意。この式で 10<sup>10</sup> 回を超える N は送りません）</label
      >
      <input
        id="{uid}-ops"
        type="text"
        class="mono"
        spellcheck="false"
        autocomplete="off"
        placeholder="例: N^2"
        bind:value={ops}
        aria-invalid={opsError !== null}
      />
      {#if opsError}<p class="field-error"><StateIcon kind="warn" />{opsError}</p>{/if}
    </div>
  </div>

  <fieldset class="sizes">
    <legend>測る N（最大 10 個）</legend>
    <div class="size-list">
      {#each SIZE_CHOICES as n (n)}
        <label class="size">
          <input
            type="checkbox"
            checked={chosen.includes(n)}
            disabled={!chosen.includes(n) && chosen.length >= 10}
            onchange={(e) => {
              const el = e.currentTarget;
              if (el instanceof HTMLInputElement) toggleSize(n, el.checked);
            }}
          /><span class="num">{groupDigits(n)}</span>
        </label>
      {/each}
    </div>
    {#if sizes === null}<p class="field-error">
        <StateIcon kind="warn" />N を 1 つ以上選びます。
      </p>{/if}
  </fieldset>

  <div class="actions">
    <button
      type="button"
      class="btn btn-primary"
      disabled={!canRun}
      aria-busy={running}
      data-testid="measure-own"
      onclick={measure}
    >
      {#if running}計測中{:else}{measured ? '再計測する' : '計測する'} <kbd>⌘ Enter</kbd>{/if}
    </button>
  </div>

  {#if notice}<p class="small" role="status">{notice}</p>{/if}
  {#if error}
    <div class="status err small" role="alert">
      <StateIcon kind="over" /><span>{error.message}</span>
      {#if error.retryable}<button type="button" class="btn btn-sm" onclick={measure}>再実行</button
        >{/if}
    </div>
  {/if}
  {#if compileError !== null}
    <div class="status err small" role="alert">
      <StateIcon kind="over" /><span>コンパイルできませんでした。</span>
    </div>
    <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
    <pre class="diag" tabindex="0">{compileError}</pre>
  {/if}

  {#if measured}
    {#if measured.noTicks}
      <p class="status small" data-testid="no-ticks">
        <StateIcon kind="warn" /><span
          >コードに <code>viz.tick()</code> がないので、ステップ数は数えていません。いちばん内側のループの中に書くと数えます。</span
        >
      </p>
    {/if}
    <MeterResults {series} measuring={running} />
    {#if firstN !== undefined}
      <details>
        <summary>N = {groupDigits(firstN)} の標準出力</summary>
        <OutputPanel mode="plain" stdout={firstOut} />
      </details>
    {/if}
  {/if}
</div>

<style>
  .own {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    padding-top: var(--space-3);
  }
  .field label,
  legend {
    display: block;
    font-size: var(--fs-sm);
    margin-bottom: var(--space-1);
  }
  .settings {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
    gap: var(--space-4);
  }
  .settings input,
  .settings select {
    width: 100%;
  }
  .mono {
    font-family: var(--font-mono);
  }
  fieldset {
    border: 0;
    padding: 0;
    margin: 0;
  }
  .size-list {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
  }
  .size {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    min-height: var(--tap-min);
    padding: 0 var(--space-3);
    border: 1px solid var(--border);
    border-radius: var(--radius-md);
    font-size: var(--fs-sm);
  }
  .actions {
    display: flex;
    gap: var(--space-3);
  }
  .small {
    font-size: var(--fs-sm);
  }
  .status {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    margin: 0;
  }
  .err {
    color: var(--err-text);
  }
  .field-error {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    color: var(--warn-text);
    font-size: var(--fs-sm);
    margin-top: var(--space-1);
  }
  .diag {
    max-height: 12rem;
    overflow: auto;
  }
</style>
